/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import {
	type ComponentType,
	Fragment,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import debounce from 'lodash/debounce';
import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import { EmptyState } from '@atlassian/search-dialog/empty-state';
import { SearchDialogContent } from '@atlassian/search-dialog/search-dialog.styled';

import { SearchErrorImage } from '../../../../article-search-component/search-dropdown/resolved-and-error-content/error-image';
import { SearchSkeletons } from '../../../../article-search-component/search-dropdown/search-skeletons';
import { messages } from '../messages';
import { ArticleResults } from './article-results';
import { PortalResults } from './portal-results';
import { RequestTypeResults } from './request-type-results';
import { SearchResultBoundary } from './search-result-section';
import { ShowMoreLink } from './show-more-link';
import type { SearchQueryProps, SearchResultStatuses } from './types';
import { SearchResultStatus, SearchResultType } from './types';

interface SplitHelpObjectSearchResultsProps {
	cloudId: string;
	helpCenterAri: string;
	queryWord: string;
}

const initialResultStatuses: SearchResultStatuses = {
	[SearchResultType.Articles]: SearchResultStatus.Pending,
	[SearchResultType.Portals]: SearchResultStatus.Pending,
	[SearchResultType.RequestTypes]: SearchResultStatus.Pending,
};

// Fixed order: Resources, then Team Portals, then Request forms.
const DEBOUNCE_DELAY_MS = 500;
const sections: { Component: ComponentType<SearchQueryProps>; type: SearchResultType }[] = [
	{ type: SearchResultType.Articles, Component: ArticleResults },
	{ type: SearchResultType.Portals, Component: PortalResults },
	{ type: SearchResultType.RequestTypes, Component: RequestTypeResults },
];

export const SplitHelpObjectSearchResults = ({
	cloudId,
	helpCenterAri,
	queryWord,
}: SplitHelpObjectSearchResultsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [debouncedQueryWord, setDebouncedQueryWordState] = useState<string>();
	const [statuses, setStatuses] = useState<SearchResultStatuses>(initialResultStatuses);
	const setDebouncedQueryWord = useMemo(
		() => debounce(setDebouncedQueryWordState, DEBOUNCE_DELAY_MS),
		[],
	);

	const updateStatus = useCallback((resultType: SearchResultType, status: SearchResultStatus) => {
		setStatuses((currentStatuses) => {
			if (currentStatuses[resultType] === status) {
				return currentStatuses;
			}

			return {
				...currentStatuses,
				[resultType]: status,
			};
		});
	}, []);

	// Stable per-type handlers so each section's status effect only re-runs when its own status
	// changes, rather than on every parent render.
	const onStatusByType: Record<SearchResultType, (status: SearchResultStatus) => void> = useMemo(
		() => ({
			[SearchResultType.Articles]: (status) => updateStatus(SearchResultType.Articles, status),
			[SearchResultType.Portals]: (status) => updateStatus(SearchResultType.Portals, status),
			[SearchResultType.RequestTypes]: (status) =>
				updateStatus(SearchResultType.RequestTypes, status),
		}),
		[updateStatus],
	);

	useEffect(() => {
		setDebouncedQueryWord(queryWord);

		return () => {
			setDebouncedQueryWord.cancel();
		};
	}, [queryWord, setDebouncedQueryWord]);

	if (debouncedQueryWord === undefined) {
		return (
			<SearchDialogContent>
				<Stack xcss={styles.stackContainer}>
					<SearchSkeletons />
				</Stack>
			</SearchDialogContent>
		);
	}

	const hasPendingResults = Object.values(statuses).includes(SearchResultStatus.Pending);
	const hasResults = Object.values(statuses).includes(SearchResultStatus.Results);
	const hasOnlyErrors = Object.values(statuses).every(
		(status) => status === SearchResultStatus.Error,
	);
	const showEmptyState = !hasPendingResults && !hasResults && !hasOnlyErrors;
	const showErrorState = !hasPendingResults && hasOnlyErrors;

	return (
		<Fragment>
			{hasPendingResults && !hasResults && <SearchSkeletons />}
			{showEmptyState && (
				<EmptyState
					title={formatMessage(messages.emptyStateTitle)}
					Image={SearchErrorImage}
					content={<FormattedMessage {...messages.emptyStateDescription} />}
				/>
			)}
			{showErrorState && (
				<EmptyState
					title={formatMessage(messages.errorTitle)}
					Image={SearchErrorImage}
					content={<FormattedMessage {...messages.errorDescription} />}
				/>
			)}
			{(hasResults || hasPendingResults) && (
				<SearchDialogContent>
					<Stack xcss={styles.stackContainer}>
						{sections.map(({ type, Component }) => (
							<SearchResultBoundary
								key={type}
								onError={() => updateStatus(type, SearchResultStatus.Error)}
							>
								{/* Once any section has resolved, still-pending sections show a single
								    skeleton row so the remaining results are clearly still loading. */}
								<Suspense fallback={hasResults ? <SearchSkeletons count={1} /> : null}>
									<Component
										cloudId={cloudId}
										helpCenterAri={helpCenterAri}
										onStatus={onStatusByType[type]}
										queryWord={debouncedQueryWord}
									/>
								</Suspense>
							</SearchResultBoundary>
						))}
						{fg('hubs_help_object_search_enter_hint') && hasResults && (
							<ShowMoreLink queryWord={queryWord} />
						)}
					</Stack>
				</SearchDialogContent>
			)}
		</Fragment>
	);
};

const styles = cssMap({
	stackContainer: {
		width: '100%',
		paddingBottom: token('space.200'),
	},
});
